import { DT, PF_HH } from '../constants';
import type { World } from '../World';
import { laneX, spawnEnemyAt } from './levelSystem';

/* ═══════════════════════════════════════════════════════════════════════════
   LE MODE SANS FIN.

   Le pendant de `levelSystem` pour une partie qui n'a pas de partition. Là où
   `levelSystem` lit une timeline écrite, celui-ci PRODUIT la sienne à la volée,
   impulsion après impulsion, et la durcit à mesure que le temps passe. Il n'y a
   pas de boss, pas d'horloge de fin : seule la mort du joueur termine la partie.

   ── Trois leviers de difficulté, tous fonction du temps ──
   Le temps écoulé `run.t` gouverne un PALIER (`stage`) qui monte d'un cran
   toutes les `ENDLESS_STAGE_SECONDS`. De ce palier découlent :

     · les ennemis ÉLIGIBLES — un ennemi du roster n'entre au tirage qu'une fois
       son `tier` atteint : le fretin d'abord, les lourds et les armes exotiques
       ensuite ;
     · le COEFFICIENT DE PV (`endlessHpScale`), qui grimpe sans plafond ;
     · l'INTERVALLE entre impulsions (`endlessInterval`), qui se resserre, et la
       TAILLE de chaque volée (`endlessBatch`), qui grossit.

   Aucun de ces leviers ne connaît d'ennemi en particulier — ajouter un archétype
   au roster ne demande pas d'y toucher, exactement comme pour `levelSystem`.

   ── Déterminisme ──
   Chaque tirage — quel ennemi, quel couloir, quelle ordonnée — passe par
   `w.rng`, seedé et haché. Deux parties de même graine produisent la même suite
   d'apparitions, condition des replays et de la re-simulation serveur.

   ⚠️ Un garde-fou de pression : sous une saturation du pool d'ennemis, on SAUTE
   l'impulsion (et on la reprogramme tôt) au lieu de forcer des apparitions qui
   déborderaient en silence. Le mode monte en difficulté par le temps, pas en
   noyant l'écran au point que le moteur jette des ennemis.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Durée d'un palier, en secondes. Toutes les 40 s, un palier de plus se
 *  débloque : ennemis plus lourds éligibles, PV plus hauts, volées plus denses. */
export const ENDLESS_STAGE_SECONDS = 40;

/** Au-delà de ce nombre d'ennemis vivants, une impulsion est sautée : le pool a
 *  sa borne (`CAP_ENEMIES`), et la franchir jetterait des apparitions sans le
 *  dire. La difficulté monte par le temps, pas par le débordement. */
const ENDLESS_SOFT_CAP = 34;

/** Palier atteint à l'instant `t` (en secondes). Entier, croissant sans borne. */
export function endlessStage(t: number): number {
  return Math.floor(t / ENDLESS_STAGE_SECONDS);
}

/** Coefficient de PV du monde à l'instant `t`. Part de 1 et grimpe de 0,30 par
 *  palier — il double vers le palier 3 (~2 min) et continue au-delà. */
export function endlessHpScale(t: number): number {
  return 1 + endlessStage(t) * 0.3;
}

/** Intervalle entre deux impulsions à l'instant `t`, en secondes. Se resserre de
 *  0,22 s par palier, sans jamais descendre sous 0,9 s — en deçà, l'écran ne se
 *  lit plus, il se subit. */
export function endlessInterval(t: number): number {
  return Math.max(0.9, 3.2 - endlessStage(t) * 0.22);
}

/** Nombre d'ennemis d'une volée à l'instant `t`. Grossit d'un tous les deux
 *  paliers, plafonné à 6 pour que le garde-fou de saturation reste la seule
 *  vraie borne. */
export function endlessBatch(t: number): number {
  return Math.min(6, 2 + Math.floor(endlessStage(t) / 2));
}

export function endlessSystem(w: World): void {
  const run = w.run;
  if (!run.endless || run.finished) return;

  run.t += DT;

  // Une seule impulsion par tick suffit : l'intervalle minimal (0,9 s) est très
  // au-dessus du pas (1/120 s), donc `run.t` ne franchit jamais deux échéances
  // dans le même tick.
  if (run.t < run.endlessNext) return;

  const stage = endlessStage(run.t);
  // Le coefficient de PV du monde suit le temps : les ennemis de cette volée —
  // et les éventuels engendrés à venir — héritent du barème courant via
  // `spawnEnemyAt`, exactement comme un niveau de campagne hérite du sien.
  run.hpScale = endlessHpScale(run.t);

  // Sous saturation : on saute la volée et on retente bientôt. On ne force jamais
  // des apparitions qui déborderaient le pool.
  if (w.enemies.count >= ENDLESS_SOFT_CAP) {
    run.endlessNext = run.t + 0.5;
    return;
  }

  const batch = endlessBatch(run.t);
  for (let k = 0; k < batch; k++) {
    const idx = pickEnemy(w, stage);
    if (idx < 0) break;
    // Couloirs intérieurs (1..5) uniquement : jamais les bords, qui acculent le
    // joueur contre le mur — la même règle que le registre impose aux vagues.
    const lane = 1 + w.rng.int(5);
    const y = PF_HH + 60 + w.rng.range(0, 120);
    spawnEnemyAt(w, idx, laneX(lane), y);
  }

  run.endlessNext = run.t + endlessInterval(run.t);
}

/** Tire un ennemi ÉLIGIBLE (palier atteint) au poids, et renvoie son index
 *  numérique — ou -1 si le roster est vide ou introuvable. Consomme un tirage
 *  RNG, toujours, pour que le flux reste identique d'une machine à l'autre. */
function pickEnemy(w: World, stage: number): number {
  const pool = w.defs.endlessPool;

  // Somme des poids des éligibles. On ne construit pas de tableau intermédiaire :
  // deux passes suffisent et n'allouent rien dans la boucle de jeu.
  let total = 0;
  for (const e of pool) {
    if (e.tier <= stage) total += e.weight;
  }
  if (total <= 0) return -1;

  let r = w.rng.int(total);
  for (const e of pool) {
    if (e.tier > stage) continue;
    r -= e.weight;
    if (r < 0) {
      const idx = w.defs.enemyByI.get(e.enemy);
      return idx === undefined ? -1 : idx;
    }
  }
  return -1;
}
